import { ApolloError } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { NOTIFICATION_TIMEOUT_LONG_8S } from '@modules/common/constants';
import { useUpdateIntegration } from '@modules/integrationsV2/hooks';
import { IntegrationsActionType } from '@modules/integrationsV2/store/integrationsActionType';
import { useIntegrationsStore } from '@modules/integrationsV2/store/integrationsStore';
import {
  getOAuthIntegrationDataInput,
  getOAuthIntegrationReauthDataInput,
  getSuccessMessageForAddOrEditIntegration,
} from '@modules/integrationsV2/utils';
import { decodeOAuthState } from '@modules/integrationsV2/utils/decodeOAuthState';
import {
  GetTenantByIdDocument,
  GetUserByEmailDocument,
  IntegrationDirection,
  useCreateIntegrationMutation,
} from 'generated/graphql';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';

const trackIntegrationConnection = createAnalyticsTracker(
  'integrationConnection',
);

interface CreateOAuthIntegrationOptions {
  redirectUri: string;
  onCompleted?: (integrationID: string) => void;
  shouldRedirect?: boolean;
}

export const useCreateOAuthIntegration = ({
  redirectUri,
  onCompleted,
  shouldRedirect = true,
}: CreateOAuthIntegrationOptions) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification, showSuccessNotification } =
    useNotificationContext();

  const navigate = useNavigate();

  const dispatch = useIntegrationsStore(({ dispatch }) => dispatch);

  const { search, hash } = window.location;

  const hashedParams = new URLSearchParams(hash.slice(1));
  const accessToken = hashedParams.get('access_token');
  const hashState = hashedParams.get('state');
  const decodedHashState = hashState ? decodeOAuthState(hashState) : null;

  const searchParams = new URLSearchParams(search);
  const code = searchParams.get('integration_code');
  const queryState = searchParams.get('state');
  const domainLocation = searchParams.get('location');
  const accountsServer = searchParams.get('accounts-server');
  const decodedQueryState = queryState ? decodeOAuthState(queryState) : null;

  // TODO: Ideally we should only deconstruct the params required for that
  // integration type rather than deconstructing all of them here

  const direction = decodedQueryState?.direction || decodedHashState?.direction;

  const channelType =
    decodedQueryState?.channelType || decodedHashState?.channelType;

  const integrationID =
    decodedQueryState?.integrationID || decodedHashState?.integrationID;

  const integrationType =
    decodedQueryState?.integrationType || decodedHashState?.integrationType;

  const authCode = code || accessToken;

  const clientID =
    decodedQueryState?.data?.clientID?.toString() ||
    decodedHashState?.data?.clientID?.toString();

  const clientSecret =
    decodedQueryState?.data?.clientSecret?.toString() ||
    decodedHashState?.data?.clientSecret?.toString();

  const dataCenter =
    decodedQueryState?.data?.dataCenter?.toString() ||
    decodedHashState?.data?.dataCenter?.toString();

  const subDomain =
    decodedQueryState?.data?.subDomain?.toString() ||
    decodedHashState?.data?.subDomain?.toString() ||
    domainLocation;

  const description =
    decodedQueryState?.data?.description?.toString() ||
    decodedHashState?.data?.description?.toString();

  const isReauthorizeIntegration = !!integrationID;

  const onCompletedCallback = (integrationID: string) => {
    if (onCompleted) {
      onCompleted(integrationID);
    }

    if (
      integrationID &&
      direction === IntegrationDirection.Inbound &&
      !isReauthorizeIntegration
    ) {
      dispatch({
        type: IntegrationsActionType.SET_VIEW,
        payload: {
          selectedIntegrationIDToUpdate: integrationID,
        },
      });
    }

    if (
      direction === IntegrationDirection.Inbound &&
      channelType &&
      integrationType
    ) {
      dispatch({
        type: IntegrationsActionType.SET_VIEW,
        payload: {
          selectedIntegrationSourceOption: {
            channelType,
            integrationType,
            integrationID,
          },
        },
      });
    }

    const addIntegrationSuccessMessage =
      channelType && integrationType && !isReauthorizeIntegration
        ? getSuccessMessageForAddOrEditIntegration({
            channelType,
            integrationType,
            action: 'add',
          })
        : null;

    if (
      addIntegrationSuccessMessage &&
      direction === IntegrationDirection.Inbound
    ) {
      showSuccessNotification({
        contentComponent: () => addIntegrationSuccessMessage,
        props: {
          isClosable: true,
          timeout: NOTIFICATION_TIMEOUT_LONG_8S,
        },
      });
    }

    if (channelType) {
      trackIntegrationConnection({
        eventName: `${channelType} connection successful`,
      });
    }

    if (shouldRedirect) {
      navigate(redirectUri);
    }
  };

  const onErrorCallback = (error: ApolloError) => {
    showErrorNotification({
      contentComponent: `Something went wrong${
        error.message ? `: ${error.message}` : ''
      }`,
      props: {},
    });

    if (channelType) {
      trackIntegrationConnection({
        eventName: `${channelType} connection failed`,
      });
    }
  };

  const [
    createIntegrationMutation,
    {
      loading: createIntegrationLoading,
      error: createIntegrationError,
      called: createIntegrationCalled,
    },
  ] = useCreateIntegrationMutation({
    refetchQueries: [GetTenantByIdDocument, GetUserByEmailDocument],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      if (data.createIntegration) {
        onCompletedCallback(data.createIntegration.id);
      }
    },
    onError: onErrorCallback,
  });

  const [
    updateIntegration,
    { status: updateIntegrationStatus, called: updateIntegrationCalled },
  ] = useUpdateIntegration({
    action: 'reauth',
    channelType: channelType,
    integrationType,
    onCompleted: () => {
      if (integrationID) {
        onCompletedCallback(integrationID);
      }
    },
  });

  const createOAuthIntegration = useCallback(async () => {
    if (
      !authCode ||
      !channelType ||
      !integrationType ||
      !direction ||
      isReauthorizeIntegration
    ) {
      return;
    }

    try {
      await createIntegrationMutation({
        variables: {
          tenantID,
          createdByUser: userID,
          channel: channelType,
          integrationType,
          direction,
          data: getOAuthIntegrationDataInput({
            channelType,
            authCode,
            clientID: clientID ?? '',
            clientSecret: clientSecret ?? '',
            dataCenter: dataCenter ?? '',
            subDomain: subDomain ?? '',
            accountsServer: accountsServer ?? '',
            redirectUri,
          }),
          integrationDescription: description
            ? {
                description,
              }
            : undefined,
        },
      });
    } catch {
      // do nothing, already handled from onError
    }
  }, [
    authCode,
    channelType,
    integrationType,
    direction,
    isReauthorizeIntegration,
    createIntegrationMutation,
    tenantID,
    userID,
    clientID,
    clientSecret,
    dataCenter,
    subDomain,
    accountsServer,
    redirectUri,
    description,
  ]);

  const reauthorizeOAuthIntegration = useCallback(async () => {
    if (!authCode || !channelType || !direction || !isReauthorizeIntegration) {
      return;
    }

    try {
      await updateIntegration({
        integrationID,
        isActive: true,
        updateAuthData: getOAuthIntegrationReauthDataInput({
          channelType,
          authCode,
          clientID: clientID ?? '',
          clientSecret: clientSecret ?? '',
          dataCenter: dataCenter ?? '',
          subDomain: subDomain ?? '',
          accountsServer: accountsServer ?? '',
          redirectUri,
        }),
      });
    } catch {
      // do nothing, already handled from onError
    }
  }, [
    accountsServer,
    authCode,
    channelType,
    clientID,
    clientSecret,
    dataCenter,
    direction,
    integrationID,
    isReauthorizeIntegration,
    redirectUri,
    subDomain,
    updateIntegration,
  ]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);

    const integrationError = queryParams.get('integration_error');
    const integrationErrorDescription = queryParams.get('error_description');

    const errorMessage = integrationErrorDescription || integrationError;

    if (errorMessage) {
      showErrorNotification({
        contentComponent: `Something went wrong: ${errorMessage}`,
        props: {},
      });
    }
  }, [showErrorNotification]);

  useEffect(() => {
    if (createIntegrationCalled) {
      return;
    }
    createOAuthIntegration();
  }, [createIntegrationCalled, createOAuthIntegration]);

  useEffect(() => {
    if (updateIntegrationCalled) {
      return;
    }
    reauthorizeOAuthIntegration();
  }, [updateIntegrationCalled, reauthorizeOAuthIntegration]);

  const createIntegrationStatus = apolloStatusToQueryStatus(
    createIntegrationLoading,
    createIntegrationCalled,
    false,
    createIntegrationError,
  );

  return (
    IsLoading(createIntegrationStatus) || IsLoading(updateIntegrationStatus)
  );
};
